import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  getGuideBonusesData,
  type BonusDefinition,
  type GuideBonusesData,
} from '@/lib/bonuses-client';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/bonuses/')({
  component: BonusesPage,
});

function formatBonusCount(count: number): string {
  return `${count} ${count === 1 ? 'bonus' : 'bonuses'}`;
}

function referencedBonusIds(data: GuideBonusesData): Set<number> {
  const ids = new Set<number>();
  const addDefinitions = (definitions: Array<{ id: number }>) => {
    definitions.forEach(definition => {
      ids.add(definition.id);
    });
  };

  data.races.forEach(race => addDefinitions(race.bonusDetails));
  data.personalities.forEach(personality => {
    addDefinitions(personality.bonusDetails);
    addDefinitions(personality.kingdomInfluenceBonusDetails);
  });
  data.advisors.forEach(advisor => addDefinitions(advisor.bonusDetails));
  data.items.forEach(item => addDefinitions(item.bonusDetails));

  return ids;
}

function BonusJumpList({ bonuses }: { bonuses: BonusDefinition[] }) {
  return (
    <Card className="p-4">
      <h2 className="font-cinzel text-lg text-[#f0f0f0]">Jump to Bonus</h2>
      <div className="mt-3 flex max-h-[320px] flex-wrap gap-2 overflow-y-auto pr-1">
        {bonuses.map(bonus => (
          <a
            key={bonus.id}
            href={`#${bonus.anchorId}`}
            className="rounded-lg border border-[#d4af37]/20 bg-[#d4af37]/8 px-2.5 py-1.5 text-sm text-[#e6c547] transition-colors hover:bg-[#d4af37]/14 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#d4af37]"
          >
            {bonus.label}
          </a>
        ))}
      </div>
    </Card>
  );
}

function BonusCard({ bonus }: { bonus: BonusDefinition }) {
  return (
    <Card id={bonus.anchorId} className="scroll-mt-28 p-4 sm:p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-cinzel text-xl text-[#f0f0f0]">{bonus.label}</h2>
            <Badge>#{bonus.id}</Badge>
          </div>
          <p className="text-base leading-7 text-[#d0d0d0]">{bonus.description}</p>
        </div>
        <a
          href={`#${bonus.anchorId}`}
          aria-label={`Deep link to ${bonus.label}`}
          className="shrink-0 rounded-lg border border-[#d4af37]/25 px-3 py-1.5 text-sm font-semibold text-[#e6c547] transition-colors hover:bg-[#d4af37]/12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#d4af37]"
        >
          #{bonus.id}
        </a>
      </div>
    </Card>
  );
}

function BonusesPage() {
  const bonusesQuery = useQuery({
    queryKey: ['guide-bonuses', 'bonus-definitions'],
    queryFn: getGuideBonusesData,
    staleTime: 1000 * 60 * 10,
  });

  const bonusIds = bonusesQuery.data ? referencedBonusIds(bonusesQuery.data) : new Set<number>();
  const bonuses = (bonusesQuery.data?.bonusDefinitions || []).filter(bonus =>
    bonusIds.has(bonus.id),
  );

  return (
    <div className="space-y-5">
      <section className="rounded-2xl border border-[#d4af37]/20 bg-[#101010]/80 p-5 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-2">
            <h1 className="font-cinzel text-3xl text-[#f0f0f0]">Bonus Reference</h1>
            <p className="max-w-3xl text-base leading-7 text-[#c8c8c8]">
              Player-facing bonus explanations used by the current age guide.
              This page keeps the named effects in one place without exposing
              the raw formula templates behind them.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {bonusesQuery.data ? <Badge>{bonusesQuery.data.ageName}</Badge> : null}
            {bonusesQuery.data ? <Badge>Source: {bonusesQuery.data.source}</Badge> : null}
            {bonuses.length ? <Badge>{formatBonusCount(bonuses.length)}</Badge> : null}
          </div>
        </div>
      </section>

      {bonusesQuery.isLoading ? (
        <Card className="p-4 text-sm text-[#b8b8b8]">Loading current age bonuses...</Card>
      ) : null}

      {bonusesQuery.isError ? (
        <Card className="p-4 text-sm text-[#b8b8b8]">
          Current age bonus data could not be loaded. The guide app needs the published bonuses and
          strings bucket manifests to render this reference.
        </Card>
      ) : null}

      {bonuses.length ? (
        <>
          <BonusJumpList bonuses={bonuses} />
          <div className="grid gap-3">
            {bonuses.map(bonus => (
              <BonusCard key={bonus.id} bonus={bonus} />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
